import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, mergeProps as _mergeProps, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withKeys as _withKeys, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "px-5 pt-6 pb-4 flex flex-col flex-1 min-h-0" };
const _hoisted_2 = { class: "flex items-center justify-between mb-6 sm:mb-8" };
const _hoisted_3 = { class: "text-2xl sm:text-3xl font-bold text-gray-900 shrink-0" };
const _hoisted_4 = { class: "space-y-1" };
import { ref, computed, watch, h, onMounted } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useTableQueryUrlSync } from "@/shared/composables/useTableQueryUrlSync";
import { handleNavigationFailure } from "@/shared/utils/navigation";
import { buildTableLabels } from "@/shared/composables/tableLabels";
import { subscriptionSearchParts } from "../composables/tableSearch";
import { matchesSearchTerm } from "@/shared/utils/tableSearch";
import { buildDonorRoute } from "../composables/donorRoute";
import { useSubscriptionOptions } from "../composables/useSubscriptionOptions";
import { subscriptionStatusTheme, subscriptionStatusIcon } from "../composables/subscriptionStatusBadge";
import SubscriptionCreateModal from "../components/SubscriptionCreateModal.vue";
import { Avatar, Badge, Button, Checkbox, DataTable, Drawer, Modal, Pager, TableFilterSelector, TableShell, TextInput, useTableQuery, useTableViews, } from "@eudonet/opal";
import { useSubscriptionStore } from "../../stores/useSubscriptionStore";
import { useSubscriptionViewPreferences } from "../composables/useSubscriptionViewPreferences";
import { SUBSCRIPTION_VIEW_PRESETS } from "../composables/subscriptionViewPresets";
import { useFinanceFormatters } from "@/shared/composables/useFinanceFormatters";
import { buildFilterSelectorLabels } from "@/shared/composables/filterSelectorLabels";
import { useListRowSelection } from "@/shared/composables/useListRowSelection";
import ListFloatActionBar from "@/shared/components/ListFloatActionBar.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'SubscriptionListView',
    setup(__props) {
        const { t } = useI18n();
        const filterSelectorLabels = computed(() => buildFilterSelectorLabels(t));
        const { formatAmount } = useFinanceFormatters();
        const store = useSubscriptionStore();
        const { toOption } = useSubscriptionOptions();
        const route = useRoute();
        const router = useRouter();
        const showCreateModal = ref(route.query.action === "new");
        const createModalMounted = ref(showCreateModal.value);
        watch(showCreateModal, (open) => {
            if (open)
                createModalMounted.value = true;
        });
        const ROW_OPTIONS = [10, 30, 50];
        // ── Create-modal URL sync (?action=new) ──────────────────────────────────────
        watch(showCreateModal, (open) => {
            const currentAction = route.query.action;
            if (open && currentAction !== "new") {
                router
                    .replace({ query: { ...route.query, action: "new" } })
                    .catch(handleNavigationFailure("SubscriptionListView:modal-open"));
            }
            else if (!open && currentAction === "new") {
                const { action: _action, ...rest } = route.query;
                router
                    .replace({ query: rest })
                    .catch(handleNavigationFailure("SubscriptionListView:modal-close"));
            }
        });
        watch(() => route.query.action, (action) => {
            showCreateModal.value = action === "new";
        });
        // ── Filter option lists (status / frequency) ─────────────────────────────────
        // Selectable values for the "Filtrer" dropdown and the clickable filter pills.
        // These are the dimensions the OPDON subscription search supports server-side
        // (status + frequency). Sourced from the SubscriptionStatus / SubscriptionFrequency
        // enums so the filter stays in sync with the entity contract.
        const SUBSCRIPTION_STATUS_VALUES = [
            "Active",
            "Paused",
            "Canceled",
            "Expired",
            "Failed",
        ];
        const SUBSCRIPTION_FREQUENCY_VALUES = [
            "Weekly",
            "Monthly",
            "Quarterly",
            "Biannual",
            "Annual",
            "Yearly",
        ];
        const statusItems = computed(() => SUBSCRIPTION_STATUS_VALUES.map((v) => ({ id: v, label: t(`finance.subscriptions.status.${v}`) })));
        const frequencyItems = computed(() => SUBSCRIPTION_FREQUENCY_VALUES.map((v) => ({
            id: v,
            label: t(`finance.subscriptions.frequency.${v}`),
        })));
        // ── Table query ──────────────────────────────────────────────────────────────
        // filterLabels names each pill's dimension ("Statut"); filterItems resolves each value id to its
        // localized label so a pill reads "Statut : Actif" / "Statut (2)" instead of raw keys.
        const tableQuery = useTableQuery({
            pageSize: ROW_OPTIONS[0],
            // Getters rather than computed(): useTableQuery declares `filterLabels`/`filterItems` as
            // MaybeRefOrGetter<Record<…>>, so either form is accepted. The getters are reactive all the
            // same — they run inside useTableQuery's activeFilterPills computed, which tracks the locale
            // read by t(). (Nothing forces the choice; the brand mismatch this comment used to cite was a
            // duplicate-Vue artefact, gone with the single-Vue `paths` pin in tsconfig.json.)
            filterLabels: () => ({
                status: t("finance.subscriptions.columns.status"),
                frequency: t("finance.subscriptions.columns.frequency"),
            }),
            filterItems: () => ({
                status: statusItems.value,
                frequency: frequencyItems.value,
            }),
        });
        // ── Load subscriptions from BFF ─────────────────────────────────────────────
        // NOTE: the OPDON subscription search filters by a single status + a single frequency. A saved
        // list carrying several values for the same key applies only the first — the same single-value
        // limitation the preset tabs already have. status + frequency are different keys (AND), so the
        // common "one status (+ one frequency)" lists filter exactly.
        async function loadSubscriptions() {
            const statusFilter = tableQuery.state.filters["status"];
            const freqFilter = tableQuery.state.filters["frequency"];
            await store.loadSubscriptions({
                status: statusFilter?.type === "in" ? statusFilter.values[0] : undefined,
                frequency: freqFilter?.type === "in" ? freqFilter.values[0] : undefined,
                page: tableQuery.state.page,
                pageSize: tableQuery.state.pageSize,
            });
        }
        onMounted(async () => {
            await loadSubscriptions();
            // Restore the user's saved prefiltered lists + active tab from OPREF.
            await restoreViewPreferences();
        });
        // Reload on query state change (filters, sort, page) — `search` is intentionally
        // excluded: it filters the already-loaded rows client-side (see `filteredSubscriptions`)
        // and must not trigger a BFF/database call.
        watch(() => ({
            filters: tableQuery.state.filters,
            page: tableQuery.state.page,
            pageSize: tableQuery.state.pageSize,
            activeViewId: tableQuery.state.activeViewId,
        }), loadSubscriptions, { deep: true });
        // ── Client-side search ────────────────────────────────────────────────────────
        // Filters the subscriptions currently loaded in the table against the search term.
        // Matches the visible cell content (id, formatted amounts and the translated
        // frequency / status labels) so the user can search exactly what they see.
        // No request is made — this is purely in-memory (see tableSearch.ts).
        const searchActive = computed(() => !!tableQuery.state.search?.trim());
        const filteredSubscriptions = computed(() => {
            const term = tableQuery.state.search ?? "";
            if (!term.trim())
                return store.subscriptions;
            return store.subscriptions.filter((s) => matchesSearchTerm(subscriptionSearchParts(s, { t, formatAmount }), term));
        });
        // When a search is active the table shows the filtered subset of the current page,
        // so the pager must reflect that subset (one page, filtered count) instead of the
        // server-side totals. With no search term the server pagination drives the pager.
        const pagerTotalItems = computed(() => searchActive.value ? filteredSubscriptions.value.length : store.totalItems);
        const pagerTotalPages = computed(() => (searchActive.value ? 1 : store.totalPages));
        const pagerCurrentPage = computed(() => (searchActive.value ? 1 : tableQuery.state.page));
        // ── Preset views (tab strip Level 1) ─────────────────────────────────────────
        const tableViews = useTableViews({
            initialViews: [
                {
                    id: "all",
                    label: t("finance.subscriptions.title"),
                    isPreset: true,
                    isDefault: true,
                    querySnapshot: {},
                },
                {
                    id: "active",
                    label: t("finance.subscriptions.status.Active"),
                    isPreset: true,
                    querySnapshot: { filters: { status: { type: "in", values: ["Active"] } } },
                },
                {
                    id: "paused",
                    label: t("finance.subscriptions.status.Paused"),
                    isPreset: true,
                    querySnapshot: { filters: { status: { type: "in", values: ["Paused"] } } },
                },
                {
                    id: "monthly",
                    label: t("finance.subscriptions.frequency.Monthly"),
                    isPreset: true,
                    querySnapshot: { filters: { frequency: { type: "in", values: ["Monthly"] } } },
                },
                {
                    id: "cancelled",
                    label: t("finance.subscriptions.status.Cancelled"),
                    isPreset: true,
                    querySnapshot: { filters: { status: { type: "in", values: ["Canceled"] } } },
                },
                {
                    id: "expired",
                    label: t("finance.subscriptions.status.Expired"),
                    isPreset: true,
                    querySnapshot: { filters: { status: { type: "in", values: ["Expired"] } } },
                },
            ],
        });
        // When a tab is activated, apply its querySnapshot filters to the shared query state.
        // flush: "sync" lets useTableQueryUrlSync's URL-driven filter overrides (which run immediately
        // after setView) land after the preset filters, not before.
        watch(() => tableQuery.state.activeViewId, (id) => {
            const view = tableViews.views.value.find((v) => v.id === id);
            const nextFilters = view?.querySnapshot?.filters;
            if (nextFilters) {
                // Deep-clone so the live query never shares references with the view's snapshot — otherwise
                // editing a filter would mutate the saved list and silently re-persist it (OP-5747).
                tableQuery.state.filters = JSON.parse(JSON.stringify(nextFilters));
            }
            else if (Object.keys(tableQuery.state.filters).length > 0) {
                tableQuery.clearAllFilters();
            }
            if (tableQuery.state.page !== 1) {
                tableQuery.setPage(1);
            }
        }, { flush: "sync" });
        // ── URL sync (preset view, filters, pagination) ─────────────────────────────
        useTableQueryUrlSync({
            tableQuery,
            syncView: true,
            defaultViewId: "all",
            defaultPageSize: ROW_OPTIONS[0],
            filterKeys: {
                status: { paramName: "status" },
                frequency: { paramName: "frequency" },
            },
        });
        // ── "Filtrer" dropdown + clickable filter pills ───────────────────────────────
        // Backs both the Level-2 "Filtrer" trigger and the Level-3 pills: TableShell emits
        // @edit-filter(key) when a pill is clicked, and we call openForField(key) to open the
        // selector on that field (the ref is bound in the #filter slot). All fields are 'list'
        // (multi-select) — selecting values applies an "in" filter via @apply.
        const filterSelectorRef = ref(null);
        const filterFields = computed(() => [
            {
                key: "status",
                label: t("finance.subscriptions.columns.status"),
                icon: "ri-pulse-line",
                type: "list",
                items: statusItems.value,
                selectAllLabel: t("table.selectAll"),
                step2SearchPlaceholder: t("finance.subscriptions.filter.searchValue"),
            },
            {
                key: "frequency",
                label: t("finance.subscriptions.columns.frequency"),
                icon: "ri-repeat-line",
                type: "list",
                items: frequencyItems.value,
                selectAllLabel: t("table.selectAll"),
                step2SearchPlaceholder: t("finance.subscriptions.filter.searchValue"),
            },
        ]);
        // 'list' fields emit @apply(key, values); convert to an "in" filter (or clear when empty).
        function handleFilterApply(key, values) {
            if (values.length > 0) {
                tableQuery.setFilter(key, { type: "in", values });
            }
            else {
                tableQuery.removeFilter(key);
            }
        }
        // ── Custom prefiltered lists ("Mes listes") persistence (OP-6176) ─────────────
        // A list stores the filter state it applies. It can be built by combining selected Eudonet
        // presets + existing lists ("Créer la liste"), or by saving the current filters ("Enregistrer").
        // Lists become tabs, can be renamed / deleted, and are persisted to OPREF (restored next session).
        const { savedViews, createListFromSelection, saveCurrentView, renameList, deleteList, restore: restoreViewPreferences, } = useSubscriptionViewPreferences({
            tableQuery,
            tableViews,
            hasUrlView: () => !!route.query.view,
        });
        // "Enregistrer" (save-view) — persist the current filter state as a new list.
        function onSaveCurrentView() {
            saveCurrentView(tableQuery.state.filters);
        }
        // ── Rename-list modal ─────────────────────────────────────────────────────────
        const renameModalOpen = ref(false);
        const renameTargetId = ref(null);
        const renameDraft = ref("");
        function openRenameList(id, currentLabel) {
            renameTargetId.value = id;
            renameDraft.value = currentLabel;
            renameModalOpen.value = true;
        }
        function confirmRenameList() {
            if (renameTargetId.value && renameDraft.value.trim()) {
                renameList(renameTargetId.value, renameDraft.value);
            }
            renameModalOpen.value = false;
        }
        // ── tabDropdowns ──────────────────────────────────────────────────────────────
        // Eudonet group = preset filter criteria (Actif, Mensuel, …). The "Mes listes" group lists the
        // user's created lists (each with a rename / delete ⋯ menu). Checking any combination across BOTH
        // groups and clicking "Créer la liste" creates one new list whose filter merges the selections.
        const tabDropdowns = computed(() => {
            const eudonetItems = SUBSCRIPTION_VIEW_PRESETS.map((p) => ({
                id: p.id,
                label: t(p.labelKey),
                group: "eudonet",
            }));
            const myItems = savedViews.value.map((v) => ({
                id: v.id,
                label: v.label,
                group: "my",
                moreActions: [
                    {
                        label: t("finance.subscriptions.views.renameList"),
                        icon: "ri-pencil-line",
                        onClick: () => openRenameList(v.id, v.label),
                    },
                    {
                        label: t("finance.subscriptions.views.deleteList"),
                        icon: "ri-delete-bin-line",
                        variant: "danger",
                        onClick: () => deleteList(v.id),
                    },
                ],
            }));
            return [
                {
                    label: t("table.tabs.myPreFilteredLists"),
                    iconLeft: "ri-equalizer-line",
                    showHeader: true,
                    selectedCountSuffix: t("table.tabs.selected"),
                    showSearch: true,
                    searchPlaceholder: t("table.tabs.searchPlaceholder"),
                    searchClearLabel: t("common.clearSearch"),
                    size: "l",
                    emptyLabel: t("table.tabs.noListCreated"),
                    groupButtons: [
                        { value: "eudonet", label: `Eudonet (${eudonetItems.length})` },
                        { value: "my", label: `${t("table.tabs.myLists")} (${myItems.length})` },
                    ],
                    items: [...eudonetItems, ...myItems],
                    footerButtons: [
                        {
                            label: t("table.tabs.createList"),
                            variant: "primary",
                            onClick: (selectedItems) => {
                                createListFromSelection(selectedItems.map((i) => i.id));
                            },
                        },
                    ],
                },
            ];
        });
        // TODO: temporarily hide the share ("Partager") button until view sharing exists.
        const tablePermissions = {
            canShareView: false,
        };
        // ── TableShell i18n labels ────────────────────────────────────────────────────
        const tableLabels = computed(() => buildTableLabels(t));
        // ── Column visibility drawer ──────────────────────────────────────────────────
        // Plain ref rather than useDrawer(): vue-tsc only auto-unwraps a directly-assigned
        // `ref()` in a `v-model` binding, not a ref destructured from a composable's return
        // object — the latter leaks `Ref<boolean>` into Drawer's `modelValue: boolean` prop.
        const columnsDrawerOpen = ref(false);
        function openColumnsDrawer() {
            columnsDrawerOpen.value = true;
        }
        const columnVisibility = ref({
            id: true,
            label: true,
            donorName: true,
            initialAmount: false,
            amount: true,
            frequency: true,
            paymentMethod: true,
            status: true,
        });
        const drawerColumns = computed(() => [
            { key: "id", label: t("finance.subscriptions.columns.id") },
            { key: "label", label: t("finance.subscriptions.columns.label") },
            { key: "donorName", label: t("finance.subscriptions.columns.donor") },
            { key: "initialAmount", label: t("finance.subscriptions.columns.initialAmount") },
            { key: "amount", label: t("finance.subscriptions.columns.amount") },
            { key: "frequency", label: t("finance.subscriptions.columns.frequency") },
            { key: "paymentMethod", label: t("finance.subscriptions.columns.paymentMethod") },
            { key: "status", label: t("finance.subscriptions.columns.status") },
        ]);
        // ── Row selection + float action bar ─────────────────────────────────────────
        const { selectColumn, effectiveCount, allPagesSelected, totalAcrossAllPages, clearSelection, onSelectAllPages } = useListRowSelection({
            t,
            rows: () => filteredSubscriptions.value,
            getRowId: (subscription) => subscription.id,
            totalCount: () => pagerTotalItems.value,
        });
        // ── Table columns: id, initialAmount, amount, frequency, status
        const subscriptionColumns = [
            {
                key: "id",
                header: t("finance.subscriptions.columns.id"),
                cell: ({ row }) => {
                    const id = row.getValue("id");
                    return h(RouterLink, {
                        to: `/subscriptions/${id}`,
                        class: "text-blue-500 hover:underline font-medium",
                        "data-testid": `subscription-row-link-${id}`,
                    }, () => id);
                },
                draggable: true,
            },
            {
                id: "label",
                header: t("finance.subscriptions.columns.label"),
                cell: ({ row }) => toOption(row.original).label,
                draggable: true,
            },
            {
                key: "donorName",
                header: t("finance.subscriptions.columns.donor"),
                cell: ({ row }) => {
                    const name = row.getValue("donorName") || "–";
                    const donorRoute = buildDonorRoute(row.original);
                    const label = donorRoute
                        ? h(RouterLink, { to: donorRoute, class: "text-blue-500 hover:underline truncate" }, () => name)
                        : h("span", { class: "truncate" }, name);
                    return h("div", { class: "flex items-center gap-2 min-w-0" }, [
                        h(Avatar, { name, class: "shrink-0", presentational: true }),
                        label,
                    ]);
                },
                draggable: true,
            },
            {
                key: "initialAmount",
                header: t("finance.subscriptions.columns.initialAmount"),
                cell: ({ row }) => formatAmount(row.original.initialAmount, row.original.currency),
                draggable: true,
            },
            {
                key: "amount",
                header: t("finance.subscriptions.columns.amount"),
                cell: ({ row }) => formatAmount(row.getValue("amount"), row.original.currency),
                draggable: true,
            },
            {
                key: "frequency",
                header: t("finance.subscriptions.columns.frequency"),
                cell: ({ row }) => {
                    const freq = row.getValue("frequency");
                    return freq ? t(`finance.subscriptions.frequency.${freq}`) : "–";
                },
                draggable: true,
            },
            {
                key: "paymentMethod",
                header: t("finance.subscriptions.columns.paymentMethod"),
                cell: ({ row }) => {
                    const pm = row.original.paymentMethod;
                    return pm ? t(`finance.subscriptions.paymentMethod.${pm}`) : "–";
                },
                draggable: true,
            },
            {
                key: "status",
                header: t("finance.subscriptions.columns.status"),
                cell: ({ row }) => {
                    const status = row.getValue("status");
                    return h(Badge, {
                        label: t(`finance.subscriptions.status.${status}`),
                        theme: subscriptionStatusTheme[status],
                        showCloseIcon: false,
                        size: "sm",
                        leftIcon: subscriptionStatusIcon[status],
                    });
                },
                draggable: true,
            },
        ];
        const columns = computed(() => [selectColumn, ...subscriptionColumns]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("h1", _hoisted_3, _toDisplayString(_ctx.$t("finance.subscriptions.title")), 1 /* TEXT */),
                    _createVNode(_unref(Button), {
                        label: _ctx.$t('finance.subscriptions.new'),
                        "icon-left": "ri-add-fill",
                        "data-testid": "subscription-list-create-button",
                        onClick: _cache[0] || (_cache[0] = ($event) => (showCreateModal.value = true))
                    }, null, 8 /* PROPS */, ["label"])
                ]),
                _createVNode(_unref(TableShell), {
                    query: _unref(tableQuery),
                    views: _unref(tableViews),
                    labels: tableLabels.value,
                    permissions: tablePermissions,
                    "tab-dropdowns": tabDropdowns.value,
                    "show-view-selector": true,
                    "search-placeholder": _ctx.$t('finance.subscriptions.title'),
                    onSaveView: onSaveCurrentView,
                    onEditFilter: _cache[2] || (_cache[2] = (key) => filterSelectorRef.value?.openForField(key))
                }, {
                    filter: _withCtx(() => [
                        _createVNode(_unref(TableFilterSelector), _mergeProps(filterSelectorLabels.value, {
                            ref_key: "filterSelectorRef",
                            ref: filterSelectorRef,
                            fields: filterFields.value,
                            "active-filters": _unref(tableQuery).state.filters,
                            "trigger-label": _ctx.$t('table.filter'),
                            "trigger-icon-left": "ri-filter-2-line",
                            "step1-search-placeholder": _ctx.$t('finance.subscriptions.filter.searchField'),
                            "confirm-label": _ctx.$t('table.tabs.apply'),
                            "back-label": _ctx.$t('common.back'),
                            "advanced-filters-label": "",
                            "no-results-label": _ctx.$t('finance.subscriptions.filter.noResults'),
                            onApply: handleFilterApply,
                            onRemoveFilter: _cache[1] || (_cache[1] = (key) => _unref(tableQuery).removeFilter(key))
                        }), null, 16 /* FULL_PROPS */, ["fields", "active-filters", "trigger-label", "step1-search-placeholder", "confirm-label", "back-label", "no-results-label"])
                    ]),
                    table: _withCtx(() => [
                        _createVNode(_unref(DataTable), {
                            columns: columns.value,
                            data: filteredSubscriptions.value,
                            "column-visibility": columnVisibility.value,
                            loading: _unref(store).isLoading,
                            "has-settings": "",
                            "sticky-header": "",
                            "empty-text": _ctx.$t('finance.subscriptions.empty'),
                            onSettingsClick: openColumnsDrawer
                        }, null, 8 /* PROPS */, ["columns", "data", "column-visibility", "loading", "empty-text"])
                    ]),
                    pagination: _withCtx(() => [
                        _createVNode(_unref(Pager), {
                            "current-page": pagerCurrentPage.value,
                            "total-pages": pagerTotalPages.value,
                            "total-items": pagerTotalItems.value,
                            "items-per-page": _unref(tableQuery).state.pageSize,
                            "rows-per-page-options": ROW_OPTIONS,
                            "rows-per-page-label": _ctx.$t('pagination.rowsPerPage'),
                            "items-count-separator": _ctx.$t('pagination.of'),
                            "previous-label": _ctx.$t('pagination.previous'),
                            "next-label": _ctx.$t('pagination.next'),
                            "go-to-page-label": _ctx.$t('pagination.goToPage'),
                            "go-label": _ctx.$t('pagination.go'),
                            "show-items-count": "",
                            "show-go-to-page": "",
                            onPageChange: _unref(tableQuery).setPage,
                            onItemsPerPageChange: _unref(tableQuery).setPageSize
                        }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "rows-per-page-label", "items-count-separator", "previous-label", "next-label", "go-to-page-label", "go-label", "onPageChange", "onItemsPerPageChange"])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["query", "views", "labels", "tab-dropdowns", "search-placeholder"]),
                _createVNode(ListFloatActionBar, {
                    "selection-count": _unref(effectiveCount),
                    "all-pages-selected": _unref(allPagesSelected),
                    "total-across-all-pages": _unref(totalAcrossAllPages),
                    onDismiss: _unref(clearSelection),
                    onSelectAllPages: _unref(onSelectAllPages)
                }, null, 8 /* PROPS */, ["selection-count", "all-pages-selected", "total-across-all-pages", "onDismiss", "onSelectAllPages"]),
                (createModalMounted.value)
                    ? (_openBlock(), _createBlock(SubscriptionCreateModal, {
                        key: 0,
                        modelValue: showCreateModal.value,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((showCreateModal).value = $event)),
                        onCreated: loadSubscriptions
                    }, null, 8 /* PROPS */, ["modelValue"]))
                    : _createCommentVNode("v-if", true),
                _createVNode(_unref(Modal), {
                    modelValue: renameModalOpen.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((renameModalOpen).value = $event)),
                    size: "sm",
                    title: _ctx.$t('finance.subscriptions.views.renameTitle')
                }, {
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _ctx.$t('common.cancel'),
                            onClick: _cache[5] || (_cache[5] = ($event) => (renameModalOpen.value = false))
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            variant: "primary",
                            label: _ctx.$t('finance.subscriptions.views.save'),
                            disabled: !renameDraft.value.trim(),
                            "data-testid": "subscription-rename-list-confirm",
                            onClick: confirmRenameList
                        }, null, 8 /* PROPS */, ["label", "disabled"])
                    ]),
                    default: _withCtx(() => [
                        _createVNode(_unref(TextInput), {
                            modelValue: renameDraft.value,
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((renameDraft).value = $event)),
                            label: _ctx.$t('finance.subscriptions.views.renameLabel'),
                            "data-testid": "subscription-rename-list-input",
                            "full-width": "",
                            onKeyup: _withKeys(confirmRenameList, ["enter"])
                        }, null, 8 /* PROPS */, ["modelValue", "label"])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "title"]),
                _createVNode(_unref(Drawer), {
                    modelValue: columnsDrawerOpen.value,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((columnsDrawerOpen).value = $event)),
                    title: _ctx.$t('finance.subscriptions.title'),
                    "aria-close-label": _ctx.$t('common.close')
                }, {
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_4, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(drawerColumns.value, (col) => {
                                return (_openBlock(), _createElementBlock("div", {
                                    key: col.key,
                                    class: "flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
                                }, [
                                    _createVNode(_unref(Checkbox), {
                                        "model-value": columnVisibility.value[col.key],
                                        label: col.label,
                                        "onUpdate:modelValue": (v) => (columnVisibility.value[col.key] = !!v)
                                    }, null, 8 /* PROPS */, ["model-value", "label", "onUpdate:modelValue"])
                                ]));
                            }), 128 /* KEYED_FRAGMENT */))
                        ])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "title", "aria-close-label"])
            ]));
        };
    }
});
